import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { presetPromptsApi } from '../../utils/presetPromptsApi';

function PromptLibrary() {
  const navigate = useNavigate();
  const [presetPrompts, setPresetPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedPrompts, setGroupedPrompts] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({}); // Track collapsed state

  useEffect(() => {
    const fetchPresetPrompts = async () => {
      try {
        setLoading(true);
        const prompts = await presetPromptsApi.getPresetPrompts();
        
        console.log('🔍 All received prompts:', prompts);
        console.log('📊 Prompts by category:', prompts.reduce((acc, p) => {
          acc[p.category] = (acc[p.category] || 0) + 1;
          return acc;
        }, {}));
        
        setPresetPrompts(prompts);
        
        // Group prompts by category, excluding pages
        const grouped = prompts.reduce((acc, prompt) => {
          if (prompt.category === 'pages') {
            console.log('🚫 Hiding pages prompt:', prompt.title);
            return acc; // Hide pages category
          }
          
          if (!acc[prompt.category]) {
            acc[prompt.category] = [];
          }
          acc[prompt.category].push(prompt);
          return acc;
        }, {});
        
        console.log('📋 Grouped prompts (after filtering):', Object.keys(grouped));
        console.log('🎯 Analytics prompts found:', grouped.analytics?.length || 0);
        
        // Sort prompts within each category by order
        Object.keys(grouped).forEach(category => {
          grouped[category].sort((a, b) => a.order - b.order);
        });
        
        setGroupedPrompts(grouped);
        
        // Initialize all sections as collapsed by default
        const initialCollapsedState = {};
        Object.keys(grouped).forEach(category => {
          initialCollapsedState[category] = true; // true = collapsed
        });
        console.log('🔒 Setting initial collapsed state:', initialCollapsedState);
        setCollapsedSections(initialCollapsedState);
      } catch (error) {
        console.error('Failed to load preset prompts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPresetPrompts();
  }, []);

  const handleTogglePrompt = async (promptId, currentEnabled) => {
    // Optimistic UI update
    const newEnabled = !currentEnabled;
    
    setPresetPrompts(prev => prev.map(prompt => 
      prompt._id === promptId 
        ? { ...prompt, enabled: newEnabled }
        : prompt
    ));
    
    setGroupedPrompts(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(category => {
        updated[category] = updated[category].map(prompt =>
          prompt._id === promptId 
            ? { ...prompt, enabled: newEnabled }
            : prompt
        );
      });
      return updated;
    });

    try {
      await presetPromptsApi.togglePresetPrompt(promptId, newEnabled);
      console.log('✅ Preset prompt toggled successfully');
      
      // Dispatch event to notify FooterBar of preset prompt changes
      window.dispatchEvent(new CustomEvent('promptsUpdated'));
    } catch (error) {
      console.error('Failed to toggle preset prompt:', error);
      // Revert UI on error
      setPresetPrompts(prev => prev.map(prompt => 
        prompt._id === promptId 
          ? { ...prompt, enabled: currentEnabled }
          : prompt
      ));
      
      setGroupedPrompts(prev => {
        const reverted = { ...prev };
        Object.keys(reverted).forEach(category => {
          reverted[category] = reverted[category].map(prompt =>
            prompt._id === promptId 
              ? { ...prompt, enabled: currentEnabled }
              : prompt
          );
        });
        return reverted;
      });
    }
  };

  const toggleSection = (category) => {
    setCollapsedSections(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoryDisplayName = (category) => {
    const names = {
      bookings: 'Bookings',
      catalog: 'Catalog',
      merch: 'Merch',
      experts: 'Experts',
      pages: 'Pages',
      analytics: 'Analytics',
      custom: 'Custom',
      general: 'General'
    };
    return names[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      bookings: '#FFFF00',
      catalog: '#17A2B8',
      merch: '#9932CC',
      experts: '#FF9500',
      pages: '#FF3B30',
      analytics: '#34C759',
      custom: '#FF3B30',
      general: '#8E8E93'
    };
    return colors[category] || '#8E8E93';
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center">
            <div className="text-white text-center py-[50px]">Loading prompt library...</div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Prompt Library</span>
            <div 
              className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
              onClick={() => navigate(-1)}
            >
              Back
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
            {Object.keys(groupedPrompts).length === 0 ? (
              <div className="text-center py-[60px]">
                <div className="text-[48px] mb-4">📚</div>
                <h3 className="text-white text-[12px] font-semibold mb-2">No Preset Prompts Available</h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px] mx-auto">
                  Preset prompts will appear here once they're added to the system.
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {Object.entries(groupedPrompts)
                  .sort(([categoryA], [categoryB]) => {
                    // Custom ordering: experts first, analytics last
                    const order = ['experts', 'bookings', 'catalog', 'merch', 'custom', 'analytics'];
                    const indexA = order.indexOf(categoryA);
                    const indexB = order.indexOf(categoryB);
                    
                    // If category not in order array, put it in the middle
                    const finalIndexA = indexA === -1 ? 999 : indexA;
                    const finalIndexB = indexB === -1 ? 999 : indexB;
                    
                    return finalIndexA - finalIndexB;
                  })
                  .map(([category, prompts]) => (
                  <div key={category}>
                    {/* Collapsible Category Header */}
                    <div 
                      className="flex items-center gap-x-[15px] mb-4 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        console.log(`🔄 Toggling ${category}: ${collapsedSections[category]} -> ${!collapsedSections[category]}`);
                        toggleSection(category);
                      }}
                    >
                      <span className="text-[12px] text-white">
                        {getCategoryDisplayName(category)}
                      </span>
                      <div 
                        className="w-[8px] h-[8px] rounded-full"
                        style={{ backgroundColor: getCategoryColor(category) }}
                      />
                      <span className="text-[rgba(255,255,255,0.5)] text-[12px]">
                        ({prompts.length})
                      </span>
                      <div className="ml-auto">
                        <svg 
                          className={`w-4 h-4 text-white transition-transform ${
                            collapsedSections[category] ? 'rotate-180' : ''
                          }`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Collapsible Category Prompts */}
                    {!collapsedSections[category] && (
                      <div className="space-y-4 mb-6">
                        {prompts.map((prompt) => (
                          <div 
                            key={prompt._id}
                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="text-white text-[12px] font-semibold mb-1">
                                  {prompt.title}
                                </h4>
                                {prompt.description && (
                                  <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-3 leading-relaxed">
                                    {prompt.description}
                                  </p>
                                )}
                              </div>
                              
                              {/* Smaller Toggle Switch with App Colors */}
                              <div className="ml-4">
                                <button
                                  onClick={() => handleTogglePrompt(prompt._id, prompt.enabled)}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                    prompt.enabled 
                                      ? 'bg-opacity-100' 
                                      : 'bg-[#39393D]'
                                  }`}
                                  style={{
                                    backgroundColor: prompt.enabled ? getCategoryColor(category) : '#39393D'
                                  }}
                                >
                                  <span
                                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                      prompt.enabled ? 'translate-x-5' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            </div>
                            
                            {/* Prompt Content Preview */}
                            <div className="bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded p-3 text-[12px] text-[rgba(255,255,255,0.8)] leading-relaxed border-l-[3px]"
                              style={{ borderLeftColor: getCategoryColor(category) }}
                            >
                              "{prompt.content}"
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Help Text with Black Overlay */}
          <div className="w-full absolute bottom-0 left-0">
            <div className="bg-black px-4 pt-8 pb-[27px]">
              <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                <p>Toggle preset prompts on or off to customize your AI experience.</p>
                <p>Enabled prompts will appear in your chat interface.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
}

export default PromptLibrary; 